/**
 * RECEPTION-OS-BUILD-V1 -- the "Recently viewed" client memory for PATIENT
 * records (founder ruling Q-06=B, 2026-08-03: recently-VIEWED patients only,
 * per-tab/session -- NEVER a browsable clinic-recent list; UAT-03 is the
 * privacy law this design serves).
 *
 * COPIES THE LEAD PATTERN EXACTLY (leads/lead-recently-viewed.ts -- D-9's
 * own instruction "copying the lead pattern exactly"; the shared-workstation
 * rule is the binding constraint):
 *
 *   - PERSISTENCE: per-tab sessionStorage; ids + timestamps ONLY; never
 *     localStorage (a shared front-desk machine must not accumulate
 *     patient-record memory across operators/tabs).
 *   - NO PHI: patient UUID + ms epoch only. No name, phone, UMR, or any
 *     patient field ever enters storage; display names are fetched at
 *     render through the EXISTING single-record read the role already
 *     lawfully holds (never a list read -- UAT-03).
 *   - NOT AN ACCESS DECISION: a stale or crafted id renders only what the
 *     server's own fenced record read returns for it (an out-of-scope id
 *     fetches not-found and renders nothing).
 *
 * Pure core + guarded storage wrappers, the lead module's shape verbatim.
 */

/** Per-tab storage key (versioned; the lead-pattern precedent). */
export const PATIENT_RECENTLY_VIEWED_STORAGE_KEY =
  'adoracare.patients.recently-viewed.v1';

/** How many opened patients the memory retains (most-recent-first). */
export const PATIENT_RECENTLY_VIEWED_CAP = 30;

/** How many entries the Home strip RENDERS (the display cap). */
export const PATIENT_RECENTLY_VIEWED_DISPLAY_CAP = 5;

export interface PatientRecentlyViewedEntry {
  readonly id: string;
  readonly at: number;
}

/** Patient ids are UUIDs; the shape check keeps storage clean of anything
 *  else (never PHI, bounded). */
const PATIENT_ID_SHAPE = /^[A-Za-z0-9-]{1,40}$/;

function isValidId(id: unknown): id is string {
  return typeof id === 'string' && PATIENT_ID_SHAPE.test(id);
}

/** Pure: fold a fresh open in (front of list, deduped, capped). */
export function applyPatientOpen(
  entries: readonly PatientRecentlyViewedEntry[],
  id: string,
  atMs: number,
  cap: number = PATIENT_RECENTLY_VIEWED_CAP,
): readonly PatientRecentlyViewedEntry[] {
  if (!isValidId(id) || !Number.isFinite(atMs)) return entries;
  const withoutId = entries.filter((e) => e.id !== id);
  return [{ id, at: atMs }, ...withoutId].slice(0, Math.max(0, cap));
}

/** Pure: parse raw storage into a clean ordered list ([] on any malformed
 *  shape -- fail-closed, never a throw). */
export function parsePatientEntries(
  raw: string | null,
): readonly PatientRecentlyViewedEntry[] {
  if (raw === null) return [];
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(value)) return [];
  const out: PatientRecentlyViewedEntry[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (item === null || typeof item !== 'object') continue;
    const rec = item as { id?: unknown; at?: unknown };
    if (
      !isValidId(rec.id) ||
      typeof rec.at !== 'number' ||
      !Number.isFinite(rec.at)
    ) {
      continue;
    }
    if (seen.has(rec.id)) continue;
    seen.add(rec.id);
    out.push({ id: rec.id, at: rec.at });
    if (out.length >= PATIENT_RECENTLY_VIEWED_CAP) break;
  }
  return out;
}

// ---- Storage wrappers (the only impure surface; try/catch-guarded) ---------

export function readRecentlyViewedPatientIds(): string[] {
  try {
    const raw = sessionStorage.getItem(PATIENT_RECENTLY_VIEWED_STORAGE_KEY);
    return parsePatientEntries(raw).map((e) => e.id);
  } catch {
    return [];
  }
}

export function recordPatientOpened(id: string, atMs: number): string[] {
  try {
    const current = parsePatientEntries(
      sessionStorage.getItem(PATIENT_RECENTLY_VIEWED_STORAGE_KEY),
    );
    const next = applyPatientOpen(current, id, atMs);
    sessionStorage.setItem(
      PATIENT_RECENTLY_VIEWED_STORAGE_KEY,
      JSON.stringify(next),
    );
    return next.map((e) => e.id);
  } catch {
    return readRecentlyViewedPatientIds();
  }
}
